import { Injectable } from '@angular/core'
import { Warehouse, WarehousesService } from '../warehouses'
import { Supplier, SuppliersService } from '../suppliers'
import {
  Observable,
  combineLatest,
  concatMap,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { SupplierOrdersService } from './supplier-orders.service'
import { Page } from '../../models/util.model'
import { parseSupplierOrderPageKeys } from './libs/supplier-order-search.lib'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  ModalResponse,
  NgModalServiceInterface,
} from '../../models/modal.model'
import { SupplierOrder } from './models/supplier-order.entity'
import {
  parseSupplierOrderModal,
  parseSupplierOrderNotification,
  SupplierOrderModalCode,
  SupplierOrderNotificationCode,
} from './libs/supplier-order-notification.lib'
import {
  SupplierOrderField,
  SupplierOrderSearchParams,
} from './models/supplier-order-search.model'
import {
  SupplierOrdersListingData,
  SupplierOrdersListingPage,
} from './models/supplier-order-listing.model'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class SupplierOrdersRepository {
  constructor(
    private supplierOrdersService: SupplierOrdersService,
    private warehousesService: WarehousesService,
    private suppliersService: SuppliersService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search supplier orders and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @returns the observable for search supplier orders
   */
  searchSupplierOrders$(
    searchParams: SupplierOrderSearchParams,
    fields: SupplierOrderField[],
    listingData?: SupplierOrdersListingData,
    returnAll?: boolean,
  ): Observable<SupplierOrdersListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.supplierOrdersService.limit

    return needsPagination
      ? this._paginateSupplierOrders$(searchParams, fields, listingData)
      : this._searchSupplierOrders$(
          searchParams,
          fields,
          listingData,
          returnAll,
        )
  }

  /**
   * Search supplier orders with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<SupplierOrdersListingPage> for search goods-returns
   */
  private _searchSupplierOrders$(
    searchParams: SupplierOrderSearchParams,
    fields: SupplierOrderField[],
    listingData?: SupplierOrdersListingData,
    returnAll?: boolean,
  ): Observable<SupplierOrdersListingPage> {
    return this.supplierOrdersService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page supplier orders data
   * @param page - the supplier orders page
   * @param fields - the supplier orders fields
   * @returns the observable for load external data
   */
  _loadExtData$(
    page: Page<SupplierOrder>,
    fields: SupplierOrderField[],
    listingData?: SupplierOrdersListingData,
  ): Observable<SupplierOrdersListingData> {
    const extDataKeys = parseSupplierOrderPageKeys(page)
    const obs$: Partial<{
      suppliers: Observable<Supplier[]>
      warehouses: Observable<Warehouse[]>
    }> = {}

    if (fields.includes('warehouseId') && extDataKeys.warehouseIds) {
      obs$['warehouses'] = this.warehousesService.store$(
        extDataKeys.warehouseIds,
        listingData?.warehouses || [],
      )
    }

    if (fields.includes('supplierId') && extDataKeys.supplierIds) {
      obs$['suppliers'] = this.suppliersService.store$(
        extDataKeys.supplierIds,
        listingData?.suppliers || [],
      )
    }

    if (!Object.keys(obs$).length) {
      return of({} as SupplierOrdersListingData)
    }

    return combineLatest(obs$)
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<SupplierOrdersListingPage> for search goods-returns
   */
  private _paginateSupplierOrders$(
    searchParams: SupplierOrderSearchParams,
    fields: SupplierOrderField[],
    listingData?: SupplierOrdersListingData,
  ): Observable<SupplierOrdersListingPage> {
    const totalCount = searchParams.limit || this.supplierOrdersService.limit
    const pagesCount = Math.ceil(totalCount / this.supplierOrdersService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.supplierOrdersService.limit,
        offset: i * this.supplierOrdersService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchSupplierOrders$(pageParams, fields, listingData),
      ),
      scan<SupplierOrdersListingPage, SupplierOrdersListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as SupplierOrdersListingPage,
      ),
      last(),
    )
  }

  notify(options: {
    code?: SupplierOrderNotificationCode
    message?: string
  }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseSupplierOrderNotification(options.code),
    )
  }

  async alert(
    code: SupplierOrderModalCode,
    supplierOrder?: SupplierOrder,
  ): Promise<ModalResponse> {
    const settings = parseSupplierOrderModal(code, supplierOrder)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
